import { Component, OnInit, NgZone } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { AbstractControl, FormGroup, FormControl } from '@angular/forms';
import { environment } from "../../../../environments/environment";

import { Map as MapboxMap, LngLatBounds } from 'mapbox-gl';
import * as mapboxgl from 'mapbox-gl';

interface MarkerStyle {
  src: string;
  width: string;
  height: string;
  offset: [number, number];
  zIndex: number;
}

// SERVICES
import { CmsService } from 'src/app/Services/cms.service';
import { ExternalService } from 'src/app/Services/external.service';

@Component({
  selector: 'app-pontos-venda',
  templateUrl: './pontos-venda.component.html',
  standalone: false

})
export class PontosVendaComponent implements OnInit{
  map!: MapboxMap;

  // Map Style && Options
  zoom = environment.mapZoom;
  center: google.maps.LatLngLiteral = {
    lat: environment.mapCenter.lat,
    lng: environment.mapCenter.lon,
  };
  
  private markerStyle: MarkerStyle = {
    src: 'assets/img/map/1x/pontos-venda.png',
    width: '45px',
    height: '59px',
    offset: [0, 0],
    zIndex: 1
  };

  language: string = '';
  defaultLanguage: string = environment.defaultLanguage;

  language_salesPoints: string = 'pt';
  salesPoints_content: string = 'linhas-pontos-vendas';
  salePointsInfo: any[] = [];
  salePointsFiltered: any[] = []

  static_content: string = 'linhas-pontos-de-venda';
  staticInfo: any = {};
  selectedSalePoint: any = null;
  // End Map Style && Options

  municipalities: any[] = [];
  searchForm: FormGroup = new FormGroup({
    municipality: new FormControl(''),
    text: new FormControl('')
  });

  // ---Mapbox---
  bounds: LngLatBounds | undefined;

  currentStyle: string = 'streets';
  mapStyle: string = 'mapbox://styles/mapbox/streets-v12';

  constructor(
    private cmsService: CmsService,
    private externalService: ExternalService,
    private router: Router,
    private translate: TranslateService,
    private title: Title,
    private zone: NgZone
    ) {
      let lang = localStorage.getItem('language') ?? this.defaultLanguage;
      this.language = lang.split('-')[0];

      this.translate.get('navLinks').pipe().subscribe(trans => {
        let pageTitle =  trans['linhas'] + ' | ' + trans['pontosVenda']
        this.title.setTitle(pageTitle);
      })
    }

  ngOnInit() {
    this.getContent()

    this.getMunicipalities();

    this.onChanges();
  }

  getContent(){
    this.cmsService.getContentList(this.static_content, this.language).subscribe(
      {
        next: data => {
          this.staticInfo = data['body']['data'];
        },
        error: er => {
          this.router.navigate(['/500']);
        }
      }
    );
  }

  getMunicipalities(){
    this.externalService.getMunicipalities().subscribe(
      {
        next: data => {

          this.municipalities.splice(0)
          
          data.forEach(element => {
            let mun = {
              name: element['pDescricao'],
              code: element['pDescricao'],
              key: element['pKey'],
              region: element['pNUTIII']
            }

            this.municipalities.push(mun)
          });

          this.municipalities = this.municipalities.sort((a: any, b: any) => a.name.localeCompare(b.name, 'pt', { sensitivity: 'base' }) );
          
          this.getSalesPoints()
        },
        error: er => {
        this.router.navigate(['/500']);
        } 
      })
  }

  getSalesPoints(){
    this.cmsService.getContentList(this.salesPoints_content, this.language_salesPoints).subscribe(
      {
        next: data => {
          let result = data['body']['data'];

          const bounds = new LngLatBounds();

          result.forEach((element: any) => {
            let coordinates = element.Coordenadas.split(',');

            const lat = Number(coordinates[0]);
            const lng = Number(coordinates[1]);

            let pos = { 
              id: element.id, 
              name: element.Nome || '', 
              address: element.Morada, 
              municipality: element.Municipio.Municipio, 
              contact: element.Contacto,
              position: { lat: lat, lng: lng }, 
              hours: element.Horario 
            };

            bounds.extend([lng, lat]);

            this.salePointsInfo.push(pos)
            
          });

          this.salePointsInfo = this.salePointsInfo.sort((a: any, b: any) => {
            const m = a.municipality.localeCompare(b.municipality, 'pt', { sensitivity: 'base' });
            if (m !== 0) return m;

            const n = a.name.localeCompare(b.name, 'pt', { sensitivity: 'base' });
            if (n !== 0) return n;

            return a.address.localeCompare(b.address, 'pt', { sensitivity: 'base' });
          });

          this.salePointsFiltered = this.salePointsInfo;

          this.bounds = bounds;

        },
        error: er => {
          this.router.navigate(['/500']);
        }
      }
    );
  }

  onChanges(){
    this.searchForm.get('municipality')?.valueChanges.subscribe(
      val => {
        this.searchSalePoint()
      }
    )

    this.searchForm.get('text')?.valueChanges.subscribe(
      val => {
        this.searchSalePoint()
      }
    )
  }

  get f(): { [key: string]: AbstractControl } {
    return this.searchForm.controls;
  }

  searchSalePoint(){
    this.selectedSalePoint = null;

    let municipality = this.searchForm.get('municipality')?.value.toLowerCase();
    let text = this.searchForm.get('text')?.value.trim().toLowerCase();

    if(municipality != '' || text != ''){
      this.salePointsFiltered = this.salePointsInfo.filter(
        (sp) => ((sp['name'].toLowerCase().includes(text) || text == '') && (municipality == '' || sp['municipality'].toLowerCase() == municipality))
      )
    } else {
      this.salePointsFiltered = this.salePointsInfo;
    }

  }

  calculateCenter(points: any[]) {
    const latitudes = points.map(point => point.position.lat);
    const longitudes = points.map(point => point.position.lng);

    const avgLat = latitudes.reduce((acc, lat) => acc + lat, 0) / latitudes.length;
    const avgLng = longitudes.reduce((acc, lng) => acc + lng, 0) / longitudes.length;

    return { lat: avgLat, lng: avgLng };
  }

  isFormValid(): boolean {
    const municipalityControl = this.searchForm.get('municipality');
    const textControl = this.searchForm.get('text');
    return municipalityControl?.value !== '' || textControl?.value !== '';
  }

  onReset(): void {
    this.searchForm.get('municipality')?.setValue('');
    this.searchForm.get('text')?.setValue('');
    this.salePointsFiltered = this.salePointsInfo;
  }

  selectEstablishment(stop: any, index: number) {

    this.zone.run(() => {
      this.selectedSalePoint = stop;
    });

    this.center = stop.position;

    if (this.map) {
      this.map.flyTo({
        center: [stop.position.lng, stop.position.lat],
        zoom: 15,
        speed: 0.8
      });
  }
  }

  resetMap() {
    this.selectedSalePoint = null;
    this.center = this.calculateCenter(this.salePointsInfo);
  }

  getSalePointStyle(marker: any, index: number): MarkerStyle {
    return this.markerStyle;
  }

  onMapLoad(event: any) {
    //console.log('💸 NEW BILLING SESSION STARTED 💸');
    this.map = event.target ? event.target : event;

    const nav = new mapboxgl.NavigationControl({
      showCompass: false,
      showZoom: true,
      visualizePitch: false
    });
    this.map.addControl(nav, 'bottom-right');
    
    const geolocate = new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true
    });
    this.map.addControl(geolocate, 'bottom-right');
  }

  changeStyle(layer: 'streets' | 'satellite') {
    this.currentStyle = layer;
    
    if (layer === 'streets') {
      this.mapStyle = 'mapbox://styles/mapbox/streets-v12';
    } else {
      // Satellite with Streets (Hybrid)
      this.mapStyle = 'mapbox://styles/mapbox/satellite-streets-v12'; 
    }
  }
}
